import { useCallback, useEffect, useState } from 'react';
import { AchievementsSummaryCard } from '../achievements/AchievementsSummaryCard';
import { fetchMyActivity, type ActivityEvent } from '../socialApi';
import { FeedCard } from '../social/FeedCard';

function ActivitySkeleton() {
  return (
    <div className="profile-activity-list" aria-hidden="true">
      {Array.from({ length: 3 }, (_, index) => (
        <div className="profile-activity-skeleton" key={index}>
          <span className="social-skeleton social-skeleton--line is-short" />
          <span className="social-skeleton social-skeleton--line is-medium" />
          <span className="social-skeleton social-skeleton--line" />
        </div>
      ))}
    </div>
  );
}

type ProfileActivitySectionProps = {
  onOpenUser: (userId: string) => void;
  onOpenVenue: (venueId: string) => void;
  includeAchievements?: boolean;
  initialLimit?: number;
};

export function ProfileActivitySection({
  onOpenUser,
  onOpenVenue,
  includeAchievements = true,
  initialLimit = 10,
}: ProfileActivitySectionProps) {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchMyActivity(null, initialLimit);
      setEvents(response.data);
      setCursor(response.meta.next_cursor);
      setHasMore(response.meta.has_more);
    } catch {
      setEvents([]);
      setCursor(null);
      setHasMore(false);
      setError('Не удалось загрузить активность');
    } finally {
      setLoading(false);
    }
  }, [initialLimit]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;

    setLoadingMore(true);
    try {
      const response = await fetchMyActivity(cursor, 10);
      setEvents((current) => [...current, ...response.data]);
      setCursor(response.meta.next_cursor);
      setHasMore(response.meta.has_more);
    } catch {
      setError('Не удалось загрузить продолжение активности');
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore]);

  return (
    <>
      {includeAchievements && <AchievementsSummaryCard />}
      <section className="profile-reference-card profile-activity-card">
        <div className="profile-reference-card__heading">
          <h2>Лента активности</h2>
        </div>

        {loading ? (
          <ActivitySkeleton />
        ) : error && events.length === 0 ? (
          <p className="profile-inline-state">{error}</p>
        ) : events.length === 0 ? (
          <p className="profile-inline-state">Активность появится после подтверждённых действий в Хоппере.</p>
        ) : (
          <>
            <div className="profile-activity-list">
              {events.map((event) => (
                <FeedCard
                  event={event}
                  key={event.id}
                  currentUserId={event.author.type !== 'venue' ? event.author.id : null}
                  onOpenUser={onOpenUser}
                  onOpenVenue={onOpenVenue}
                />
              ))}
            </div>
            {hasMore && (
              <button className="profile-secondary-button profile-load-more" disabled={loadingMore} type="button" onClick={() => void loadMore()}>
                {loadingMore ? 'Загружаем…' : 'Показать ещё'}
              </button>
            )}
          </>
        )}
      </section>
    </>
  );
}
